import { clientDailyMetrics } from "@/api/db/schema/client-daily-metrics";
import {
  mealDateSchema,
  saveWorkoutMealSchema,
  workoutMeals,
} from "@/api/db/schema/workout-meals";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

export const saveWorkoutMealFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(saveWorkoutMealSchema)
  .handler(async ({ context, data }) => {
    const { userProgramService } = await import(
      "@/api/services/user-program.service"
    );
    return userProgramService.saveWorkoutMeal(context.auth.userId, data);
  });
export const getWorkoutMealsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ date: mealDateSchema }))
  .handler(async ({ context, data }) => {
    const { clientDailyMetricsService } = await import(
      "@/api/services/client-daily-metrics.service"
    );
    return clientDailyMetricsService.getWorkoutMeals(
      context.auth.userId,
      data.date,
    );
  });
export const getWorkoutMealsOptions = (date: string) =>
  queryOptions({
    queryKey: ["workoutMeals", date],
    queryFn: () => getWorkoutMealsFn({ data: { date } }),
  });
export const uploadMealPhotoFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(
    z.object({
      contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
      size: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024),
    }),
  )
  .handler(async ({ context, data }) => {
    const { mealPhotoService } = await import(
      "@/api/services/meal-photo.service"
    );
    return mealPhotoService.createUpload(
      context.auth.userId,
      data.contentType,
      data.size,
    );
  });
export const getMealPhotoFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ key: z.string().max(300) }))
  .handler(async ({ context, data }) => {
    const { mealPhotoService, ownsMealPhoto } = await import(
      "@/api/services/meal-photo.service"
    );
    const { db } = await import("@/api/db/db.index");
    const { eq, or, sql } = await import("drizzle-orm");
    if (
      context.auth.role !== "admin" &&
      !ownsMealPhoto(data.key, context.auth.userId)
    )
      throw new Error("Bu fotoğrafa erişim yetkiniz yok.");
    const [meal] = await db
      .select({ id: workoutMeals.mealId })
      .from(workoutMeals)
      .where(
        sql`json_extract(${workoutMeals.data}, '$.photoKey') = ${data.key}`,
      )
      .limit(1);
    const [metric] = await db
      .select({ id: clientDailyMetrics.metricId })
      .from(clientDailyMetrics)
      .where(
        or(
          eq(
            sql`json_extract(${clientDailyMetrics.preWorkoutMeal}, '$.photoKey')`,
            data.key,
          ),
          eq(
            sql`json_extract(${clientDailyMetrics.postWorkoutMeal}, '$.photoKey')`,
            data.key,
          ),
        ),
      )
      .limit(1);
    if (!meal && !metric) throw new Error("Fotoğraf kaydı bulunamadı.");
    return { url: await mealPhotoService.getPhotoUrl(data.key) };
  });

export const getClientWorkoutMealsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ clientId: z.string().min(1) }))
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin")
      throw new Error("Bu danışan verilerine erişim yetkiniz yok.");
    const { db } = await import("@/api/db/db.index");
    const { eq, desc, or, isNotNull, and } = await import("drizzle-orm");
    return db
      .select({
        date: clientDailyMetrics.date,
        preWorkoutMeal: clientDailyMetrics.preWorkoutMeal,
        postWorkoutMeal: clientDailyMetrics.postWorkoutMeal,
      })
      .from(clientDailyMetrics)
      .where(
        and(
          eq(clientDailyMetrics.clientId, data.clientId),
          or(
            isNotNull(clientDailyMetrics.preWorkoutMeal),
            isNotNull(clientDailyMetrics.postWorkoutMeal),
          ),
        ),
      )
      .orderBy(desc(clientDailyMetrics.date))
      .limit(30);
  });
